import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, Bot, User, RotateCcw, CheckCircle2, Sparkles, UserCheck, Volume2, VolumeX, Loader2, Mic, MicOff } from "lucide-react";
import { useT } from "../i18n";
import { BOT } from "../constants/testIds";
import RotatingLogo from "./RotatingLogo";

// session id persisted per browser tab
function newSessionId() {
    return `chat-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export default function BotSimulator({ lang, API }) {
    const T = useT(lang);
    const [sessionId, setSessionId] = useState(newSessionId);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [sending, setSending] = useState(false);
    const [settings, setSettings] = useState(null);
    const [registering, setRegistering] = useState(false);
    const [registered, setRegistered] = useState(null);
    const [usedChips, setUsedChips] = useState(new Set()); // indexes of chips already used
    // Voice (Noor speaks bot replies via HeyGen TTS). Persist preference across reloads.
    const [voiceOn, setVoiceOn] = useState(() => {
        try { return localStorage.getItem("noorVoiceOn") === "1"; } catch { return false; }
    });
    const [voiceLoadingIdx, setVoiceLoadingIdx] = useState(null); // which message is being spoken
    const audioRef = useRef(null);
    // Voice input (mic → Whisper STT)
    const [isRecording, setIsRecording] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const mediaRecorderRef = useRef(null);
    const mediaStreamRef = useRef(null);
    const audioChunksRef = useRef([]);
    const scrollRef = useRef(null);

    useEffect(() => {
        axios.get(`${API}/bot/settings`).then((r) => {
            setSettings(r.data);
            const welcome = lang === "ar" ? r.data.welcomeMessageAr : r.data.welcomeMessageHe;
            setMessages([{ sender: "bot", text: welcome, timestamp: new Date().toISOString() }]);
        }).catch(() => {
            setMessages([{ sender: "bot", text: T.botGreeting, timestamp: new Date().toISOString() }]);
        });
        // eslint-disable-next-line
    }, []);

    // Update welcome on language change (only if at start)
    useEffect(() => {
        if (settings && messages.length === 1 && messages[0].sender === "bot") {
            const welcome = lang === "ar" ? settings.welcomeMessageAr : settings.welcomeMessageHe;
            setMessages([{ sender: "bot", text: welcome, timestamp: new Date().toISOString() }]);
        }
        // eslint-disable-next-line
    }, [lang]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, sending]);

    // Persist voice preference
    useEffect(() => {
        try { localStorage.setItem("noorVoiceOn", voiceOn ? "1" : "0"); } catch (_e) { /* ignore */ }
        // If turning off, stop any playback
        if (!voiceOn && audioRef.current) {
            try { audioRef.current.pause(); } catch (_e) { /* ignore */ }
            setVoiceLoadingIdx(null);
        }
    }, [voiceOn]);

    // Stop audio + release mic on unmount
    useEffect(() => () => {
        try { audioRef.current && audioRef.current.pause(); } catch (_e) { /* ignore */ }
        try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_e) { /* ignore */ }
    }, []);

    const speak = async (text, msgIdx) => {
        if (!text || !text.trim()) return;
        try {
            // Stop any current playback
            if (audioRef.current) {
                try { audioRef.current.pause(); } catch (_e) { /* ignore */ }
            }
            setVoiceLoadingIdx(msgIdx);
            const r = await axios.post(`${API}/noor/tts`, { text, language: lang, speed: 0.95 });
            const url = r.data?.audio_url;
            if (!url) { setVoiceLoadingIdx(null); return; }
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => setVoiceLoadingIdx(null);
            audio.onerror = () => setVoiceLoadingIdx(null);
            audio.play().catch(() => setVoiceLoadingIdx(null));
        } catch {
            setVoiceLoadingIdx(null);
        }
    };

    const toggleSpeak = (text, msgIdx) => {
        // If already playing this message, stop it
        if (voiceLoadingIdx === msgIdx && audioRef.current && !audioRef.current.paused) {
            try { audioRef.current.pause(); } catch (_e) { /* ignore */ }
            setVoiceLoadingIdx(null);
            return;
        }
        speak(text, msgIdx);
    };

    // ===== Voice input (record → Whisper) =====
    const startRecording = async () => {
        if (isRecording || sending || isTranscribing) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaStreamRef.current = stream;
            // Pick the best supported mime type
            const mimeCandidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", ""];
            let mimeType = "";
            for (const m of mimeCandidates) {
                if (!m || (window.MediaRecorder && MediaRecorder.isTypeSupported(m))) {
                    mimeType = m;
                    break;
                }
            }
            const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
            audioChunksRef.current = [];
            mr.ondataavailable = (e) => {
                if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
            };
            mr.onstop = async () => {
                const blob = new Blob(audioChunksRef.current, { type: mr.mimeType || "audio/webm" });
                // Release the mic
                try { mediaStreamRef.current?.getTracks().forEach((t) => t.stop()); } catch (_e) { /* ignore */ }
                mediaStreamRef.current = null;
                if (blob.size < 1000) return; // ignore accidental taps
                await transcribeAndSend(blob);
            };
            mediaRecorderRef.current = mr;
            mr.start();
            setIsRecording(true);
        } catch (e) {
            const msg = lang === "ar"
                ? "لم أتمكن من الوصول إلى الميكروفون. الرجاء منح الإذن ثم المحاولة مجدداً."
                : "לא הצלחתי לגשת למיקרופון. אנא אפשר גישה ונסה שוב.";
            setMessages((m) => [...m, { sender: "bot", text: msg, timestamp: new Date().toISOString() }]);
        }
    };

    const stopRecording = () => {
        if (!isRecording) return;
        try { mediaRecorderRef.current?.stop(); } catch (_e) { /* ignore */ }
        setIsRecording(false);
    };

    const transcribeAndSend = async (blob) => {
        setIsTranscribing(true);
        try {
            const fd = new FormData();
            fd.append("file", blob, "recording.webm");
            fd.append("language", lang);
            const r = await axios.post(`${API}/noor/stt`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const text = (r.data?.text || "").trim();
            if (text) {
                // Auto-turn on voice output when the user speaks (natural voice→voice loop)
                if (!voiceOn) setVoiceOn(true);
                await send(text);
            }
        } catch (_e) {
            const msg = lang === "ar"
                ? "لم أستطع فهم التسجيل. الرجاء المحاولة مرة أخرى."
                : "לא הצלחתי לפענח את ההקלטה. נסה שוב.";
            setMessages((m) => [...m, { sender: "bot", text: msg, timestamp: new Date().toISOString() }]);
        } finally {
            setIsTranscribing(false);
        }
    };

    const send = async (override) => {
        const text = (override !== undefined ? override : input).trim();
        if (!text || sending) return;
        setInput("");
        const userMsg = { sender: "user", text, timestamp: new Date().toISOString() };
        setMessages((m) => [...m, userMsg]);
        setSending(true);
        try {
            let utm = null; let source = null;
            try {
                utm = JSON.parse(sessionStorage.getItem("aa_utm") || "null");
                source = sessionStorage.getItem("aa_source") || null;
            } catch { /* ignore */ }
            const r = await axios.post(`${API}/bot/chat`, { sessionId, message: text, language: lang, source, utm });
            const botMsg = { sender: "bot", text: r.data.reply, timestamp: new Date().toISOString() };
            setMessages((m) => {
                const next = [...m, botMsg];
                if (voiceOn) speak(botMsg.text, next.length - 1);
                return next;
            });
        } catch (e) {
            const errMsg = lang === "ar"
                ? "حدث خطأ مؤقت في الاتصال. الرجاء المحاولة مرة أخرى."
                : "אירעה שגיאה זמנית בתקשורת. אנא נסה שוב.";
            setMessages((m) => [...m, { sender: "bot", text: errMsg, timestamp: new Date().toISOString() }]);
        } finally {
            setSending(false);
        }
    };

    const completeRegistration = async () => {
        if (registering) return;
        setRegistering(true);
        try {
            const r = await axios.post(`${API}/bot/extract-lead`, { sessionId });
            setRegistered(r.data);
            if (r.data?.success) {
                const successMsg = lang === "ar"
                    ? `تم تسجيلك بنجاح! 🎉\nسيتواصل معك مستشار خلال 24 ساعة.\n\nشكراً ${r.data.lead.fullName}.`
                    : `נרשמת בהצלחה! 🎉\nיועץ לימודים יחזור אליך תוך 24 שעות.\n\nתודה רבה ${r.data.lead.fullName}.`;
                setMessages((m) => [...m, { sender: "bot", text: successMsg, timestamp: new Date().toISOString() }]);
            } else {
                const errMsg = lang === "ar"
                    ? `لم أتمكن من إكمال التسجيل بعد. الرجاء تزويدي بالاسم الكامل ورقم الهاتف على الأقل.`
                    : `לא הצלחתי להשלים את הרישום עדיין. אנא ספק לי לפחות שם מלא וטלפון.`;
                setMessages((m) => [...m, { sender: "bot", text: errMsg, timestamp: new Date().toISOString() }]);
            }
        } catch (e) {
            const errMsg = lang === "ar" ? "خطأ في الخادم، حاول لاحقاً." : "שגיאה בשרת, נסה שוב מאוחר יותר.";
            setMessages((m) => [...m, { sender: "bot", text: errMsg, timestamp: new Date().toISOString() }]);
        } finally {
            setRegistering(false);
        }
    };

    const reset = () => {
        const sid = newSessionId();
        setSessionId(sid);
        setRegistered(null);
        setUsedChips(new Set());
        const welcome = settings
            ? (lang === "ar" ? settings.welcomeMessageAr : settings.welcomeMessageHe)
            : T.botGreeting;
        setMessages([{ sender: "bot", text: welcome, timestamp: new Date().toISOString() }]);
    };

    const placeholder = lang === "ar" ? "اكتب هنا..." : "כתוב כאן...";

    // Suggestion chips — stay visible the whole conversation; each disappears once clicked
    const SUGGESTION_CHIPS = lang === "ar"
        ? [
            { label: "ما هي شروط القبول؟", msg: "ما هي شروط القبول؟" },
            { label: "كم سنة الدراسة؟", msg: "كم سنة الدراسة؟" },
            { label: "هل يوجد بسيخومتري؟", msg: "هل يوجد بسيخومتري؟" },
            { label: "وين التعليم؟", msg: "أين يقع مقر الكلية ومكان الدراسة؟" },
            { label: "في سفر منظم؟", msg: "هل يوجد سفر منظم (مواصلات) للطلاب؟" },
            { label: "التخصصات بعد اللقب", msg: "ما هي التخصصات الممكنة بعد اللقب الأول؟" },
            { label: "🩺 تمريض", msg: "أريد معلومات عن مسار التمريض" },
            { label: "🩻 أشعة", msg: "أريد معلومات عن مسار الأشعة" },
            { label: "📚 تدريس", msg: "أريد معلومات عن شهادة التدريس" },
            { label: "🎓 M.Teach (للمتفوقين)", msg: "أريد معلومات عن مسار M.Teach الممول من وزارة التربية" },
            { label: "💰 المنح والأقساط", msg: "ما هي المنح المتاحة وكم الأقساط الدراسية؟" },
            { label: "✍️ أريد التسجيل", msg: "أريد التسجيل في الكلية، كيف أبدأ؟" },
        ]
        : [
            { label: "מה תנאי הקבלה?", msg: "מה תנאי הקבלה?" },
            { label: "כמה שנים התואר?", msg: "כמה שנים התואר?" },
            { label: "האם צריך פסיכומטרי?", msg: "האם צריך פסיכומטרי?" },
            { label: "איפה הלימודים?", msg: "איפה ממוקמת המכללה והיכן מתקיימים הלימודים?" },
            { label: "האם יש הסעות?", msg: "האם יש הסעות מאורגנות לסטודנטים?" },
            { label: "מסלולי המשך", msg: "אילו מסלולי המשך אפשריים אחרי התואר הראשון?" },
            { label: "🩺 סיעוד", msg: "אני רוצה מידע על מסלול סיעוד" },
            { label: "🩻 רנטגן", msg: "אני רוצה מידע על מסלול רנטגן/הדמיה" },
            { label: "📚 הוראה", msg: "אני רוצה מידע על תעודת הוראה" },
            { label: "🎓 M.Teach (מצטיינים)", msg: "אני רוצה מידע על M.Teach (תואר שני ממומן ע\"י משרד החינוך)" },
            { label: "💰 מלגות ושכ\"ל", msg: "אילו מלגות יש ומה גובה שכר הלימוד?" },
            { label: "✍️ אני רוצה להירשם", msg: "אני רוצה להירשם למכללה, איך מתחילים?" },
        ];

    const handleChipClick = (idx, msg) => {
        setUsedChips((prev) => {
            const next = new Set(prev);
            next.add(idx);
            return next;
        });
        send(msg);
    };

    const collegeName = settings
        ? (lang === "ar" ? settings.collegeNameAr : settings.collegeNameHe)
        : (lang === "ar" ? "الأهلية" : "אל-אהלייה");

    return (
        <div className="fade-up max-w-3xl mx-auto">
            <div className="text-center mb-3 md:mb-6">
                <h2 className="font-serif text-2xl md:text-4xl font-bold text-[#1A365D] gold-underline inline-block">
                    {T.navSimulator}
                </h2>
                <p className="hidden md:flex text-sm text-[#475569] mt-3 items-center justify-center gap-1.5">
                    <Sparkles size={14} color="#D4AF37" />
                    {lang === "ar"
                        ? "مدعوم بالذكاء الاصطناعي · Claude Sonnet 4.5"
                        : "מופעל ע\"י בינה מלאכותית · Claude Sonnet 4.5"}
                </p>
            </div>

            <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
                {/* Chat header */}
                <div className="bg-[#1A365D] px-3 md:px-5 py-3 md:py-4 flex items-center gap-2 md:gap-3">
                    <RotatingLogo size="sm" lang={lang} showName={false} />
                    <div className="flex-1 min-w-0">
                        <div className="text-white font-serif font-bold text-sm md:text-base truncate">{collegeName}</div>
                        <div className="text-[10px] md:text-xs text-[#D4AF37] flex items-center gap-1.5 truncate">
                            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse shrink-0" />
                            <span className="truncate">{lang === "ar" ? "متصل · بأي لغة" : "מחובר · בכל שפה"}</span>
                        </div>
                    </div>
                    <button
                        onClick={() => setVoiceOn((v) => !v)}
                        className={`text-xs flex items-center gap-1 px-2 py-1 rounded-full transition-colors ${voiceOn ? "bg-[#D4AF37]/20 text-[#D4AF37]" : "text-white/70 hover:text-white"}`}
                        title={voiceOn
                            ? (lang === "ar" ? "إيقاف صوت نور" : "השתק את קול נור")
                            : (lang === "ar" ? "تشغيل صوت نور" : "הפעל את קול נור")}
                        data-testid="bot-voice-toggle"
                    >
                        {voiceOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
                        <span className="hidden md:inline">{voiceOn ? (lang === "ar" ? "الصوت" : "קול") : (lang === "ar" ? "صامت" : "מושתק")}</span>
                    </button>
                    <button
                        onClick={reset}
                        className="text-white/80 hover:text-white text-xs flex items-center gap-1"
                        title={T.botRestart}
                        data-testid="bot-reset"
                    >
                        <RotateCcw size={14} />
                        {lang === "ar" ? "محادثة جديدة" : "שיחה חדשה"}
                    </button>
                </div>

                {/* Messages */}
                <div
                    ref={scrollRef}
                    className="p-3 md:p-5 space-y-3 h-[55vh] min-h-[320px] max-h-[480px] overflow-y-auto bg-[#FAFAFA]"
                    data-testid="bot-messages-container"
                >
                    {messages.map((m, i) => (
                        <div
                            key={i}
                            className={`flex gap-2 ${m.sender === "user" ? "justify-start" : "justify-end"}`}
                            style={{ flexDirection: m.sender === "user" ? "row" : "row-reverse" }}
                        >
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${m.sender === "user" ? "bg-[#1A365D]" : "bg-[#D4AF37]"}`}>
                                {m.sender === "user" ? <User size={16} color="#fff" /> : <Bot size={16} color="#1A365D" />}
                            </div>
                            <div className={m.sender === "user" ? "bubble-user" : "bubble-bot"}>
                                <div className="text-sm leading-relaxed whitespace-pre-wrap">{m.text}</div>
                                {m.sender === "bot" && (
                                    <button
                                        onClick={() => toggleSpeak(m.text, i)}
                                        className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-[#8B6F1E] hover:text-[#D4AF37] transition-colors"
                                        title={lang === "ar" ? "استمع بصوت نور" : "האזן בקול נור"}
                                        data-testid={`bot-msg-speak-${i}`}
                                    >
                                        {voiceLoadingIdx === i
                                            ? <Loader2 size={11} className="animate-spin" />
                                            : <Volume2 size={11} />}
                                        <span>{lang === "ar" ? "استمع" : "האזן"}</span>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}

                    {sending && (
                        <div className="flex gap-2 justify-end" style={{ flexDirection: "row-reverse" }}>
                            <div className="w-8 h-8 rounded-full bg-[#D4AF37] flex items-center justify-center shrink-0">
                                <Bot size={16} color="#1A365D" />
                            </div>
                            <div className="bubble-bot">
                                <div className="flex gap-1 items-center py-1">
                                    <span className="w-2 h-2 bg-[#1A365D] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                                    <span className="w-2 h-2 bg-[#1A365D] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                                    <span className="w-2 h-2 bg-[#1A365D] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                                </div>
                            </div>
                        </div>
                    )}

                    {registered?.success && (
                        <div className="mx-auto max-w-md text-center mt-4" data-testid={BOT.successMsg}>
                            <div className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-800 px-3 py-1.5 rounded-full text-xs font-semibold">
                                <CheckCircle2 size={14} />
                                {lang === "ar" ? "تم إنشاء العميل في النظام" : "ליד נוצר בהצלחה במערכת"}
                            </div>
                        </div>
                    )}
                </div>

                {/* Suggestion chips — visible throughout the conversation; each disappears once used */}
                {(() => {
                    const remaining = SUGGESTION_CHIPS
                        .map((c, i) => ({ ...c, _idx: i }))
                        .filter((c) => !usedChips.has(c._idx));
                    if (remaining.length === 0) return null;
                    return (
                        <div className="px-3 pt-2 pb-2 border-t border-[#F1F5F9] bg-white max-h-[90px] md:max-h-[120px] overflow-y-auto">
                            <div className="flex flex-wrap gap-1.5" data-testid="suggestion-chips">
                                {remaining.map((c) => (
                                    <button
                                        key={c._idx}
                                        onClick={() => handleChipClick(c._idx, c.msg)}
                                        disabled={sending}
                                        className="btn-outline !py-1 !px-2.5 !text-[11px] disabled:opacity-50"
                                        data-testid={BOT.quickReply(c._idx)}
                                    >
                                        {c.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })()}

                {/* Input row */}
                <div className="border-t border-[#E2E8F0] p-3 bg-white">
                    {/* Complete-registration button on its own row so the textarea gets full width */}
                    <button
                        onClick={completeRegistration}
                        disabled={registering || sending || messages.length < 3 || registered?.success}
                        className="btn-gold w-full !py-1.5 !px-3 !text-xs mb-2 disabled:opacity-50"
                        title={lang === "ar" ? "أكمل التسجيل بناءً على المحادثة" : "השלם רישום על בסיס השיחה"}
                        data-testid={BOT.leadSubmitBtn}
                    >
                        <UserCheck size={14} />
                        {registering
                            ? "..."
                            : (lang === "ar" ? "أكمل التسجيل بناءً على المحادثة" : "השלם רישום על בסיס השיחה")}
                    </button>
                    <div className="flex gap-2 items-stretch">
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    send();
                                }
                            }}
                            rows={2}
                            className="input-royal flex-1 !py-3 !text-base leading-relaxed resize-none"
                            placeholder={isRecording
                                ? (lang === "ar" ? "جارٍ التسجيل... اضغط لإيقافه" : "מקליט... לחץ לעצירה")
                                : (isTranscribing
                                    ? (lang === "ar" ? "جارٍ التفريغ..." : "מתמלל...")
                                    : placeholder)}
                            data-testid={BOT.messageInput}
                            disabled={sending || isRecording || isTranscribing}
                        />
                        <div className="flex flex-col gap-2 shrink-0">
                            <button
                                onClick={isRecording ? stopRecording : startRecording}
                                disabled={sending || isTranscribing}
                                className={`btn-royal !px-3 flex-1 ${isRecording ? "!bg-red-600 hover:!bg-red-700 animate-pulse" : ""}`}
                                title={isRecording
                                    ? (lang === "ar" ? "إيقاف التسجيل" : "עצור הקלטה")
                                    : (lang === "ar" ? "تحدث مع نور" : "דבר עם נור")}
                                data-testid="bot-mic-btn"
                            >
                                {isTranscribing
                                    ? <Loader2 size={18} className="animate-spin" />
                                    : isRecording
                                        ? <MicOff size={18} />
                                        : <Mic size={18} />}
                            </button>
                            <button
                                onClick={() => send()}
                                disabled={sending || !input.trim() || isRecording || isTranscribing}
                                className="btn-royal !px-3 flex-1"
                                data-testid={BOT.sendBtn}
                            >
                                <Send size={18} />
                            </button>
                        </div>
                    </div>
                    <div className="mt-2 text-[10px] text-[#94A3B8] text-center">
                        {isRecording
                            ? (lang === "ar" ? "🔴 نور تسمعك الآن..." : "🔴 נור מקשיבה עכשיו...")
                            : (lang === "ar"
                                ? "البوت يجيب بالعبرية والعربية والإنجليزية · تحدث أو اكتب"
                                : "הבוט עונה בעברית, ערבית ואנגלית · דבר או כתוב")}
                    </div>
                </div>
            </div>
        </div>
    );
}
